import React from "react";

/**
 * Persisted per-user unread counters (channel + DM). Not synced across devices.
 */

/** @param {string} userLower */
export function loadUnread(userLower) {
  if (!userLower) return { channels: {}, dms: {} };
  try {
    const raw = localStorage.getItem(`chat_unread_v1_${userLower}`);
    if (!raw) return { channels: {}, dms: {} };
    const p = JSON.parse(raw);
    return {
      channels: typeof p.channels === "object" && p.channels ? { ...p.channels } : {},
      dms: typeof p.dms === "object" && p.dms ? { ...p.dms } : {},
    };
  } catch {
    return { channels: {}, dms: {} };
  }
}

/** @param {string} userLower @param {{ channels: Record<string, number>, dms: Record<string, number> }} state */
export function saveUnread(userLower, state) {
  if (!userLower) return;
  try {
    localStorage.setItem(`chat_unread_v1_${userLower}`, JSON.stringify(state));
  } catch {
    /* ignore quota */
  }
}

export function makeChannelUnreadKey(guildId, channelId) {
  return `${guildId}|${channelId}`;
}

/**
 * @param {React.Dispatch<React.SetStateAction<{ channels: Record<string, number>, dms: Record<string, number> }>>} setState
 * @param {string} userLower
 * @param {string} guildId
 * @param {string} channelId
 */
export function bumpChannelUnread(setState, userLower, guildId, channelId) {
  if (!userLower || !guildId || !channelId) return;
  const ck = makeChannelUnreadKey(guildId, channelId);
  setState((prev) => {
    const next = {
      channels: { ...prev.channels, [ck]: Math.min(99, (prev.channels[ck] || 0) + 1) },
      dms: { ...prev.dms },
    };
    saveUnread(userLower, next);
    return next;
  });
}

/**
 * @param {React.Dispatch<React.SetStateAction<{ channels: Record<string, number>, dms: Record<string, number> }>>} setState
 * @param {string} userLower
 * @param {string} guildId
 * @param {string} channelId
 */
export function clearChannelUnread(setState, userLower, guildId, channelId) {
  if (!userLower || !guildId || !channelId) return;
  const ck = makeChannelUnreadKey(guildId, channelId);
  setState((prev) => {
    if (!prev.channels[ck]) return prev;
    const channels = { ...prev.channels };
    delete channels[ck];
    const next = { ...prev, channels };
    saveUnread(userLower, next);
    return next;
  });
}

/**
 * @param {React.Dispatch<React.SetStateAction<{ channels: Record<string, number>, dms: Record<string, number> }>>} setState
 * @param {string} userLower
 * @param {string} guildId
 */
export function clearGuildChannelUnreads(setState, userLower, guildId) {
  if (!userLower || !guildId) return;
  const prefix = `${guildId}|`;
  setState((prev) => {
    const channels = { ...prev.channels };
    let changed = false;
    for (const k of Object.keys(channels)) {
      if (k.startsWith(prefix)) {
        delete channels[k];
        changed = true;
      }
    }
    if (!changed) return prev;
    const next = { ...prev, channels };
    saveUnread(userLower, next);
    return next;
  });
}

/**
 * @param {React.Dispatch<React.SetStateAction<{ channels: Record<string, number>, dms: Record<string, number> }>>} setState
 * @param {string} userLower
 * @param {string} peerLower
 */
export function bumpDmUnread(setState, userLower, peerLower) {
  if (!userLower || !peerLower) return;
  setState((prev) => {
    const next = {
      channels: { ...prev.channels },
      dms: { ...prev.dms, [peerLower]: Math.min(99, (prev.dms[peerLower] || 0) + 1) },
    };
    saveUnread(userLower, next);
    return next;
  });
}

/**
 * @param {React.Dispatch<React.SetStateAction<{ channels: Record<string, number>, dms: Record<string, number> }>>} setState
 * @param {string} userLower
 * @param {string} peerLower
 */
export function clearDmUnread(setState, userLower, peerLower) {
  if (!userLower || !peerLower) return;
  setState((prev) => {
    if (!prev.dms[peerLower]) return prev;
    const dms = { ...prev.dms };
    delete dms[peerLower];
    const next = { ...prev, dms };
    saveUnread(userLower, next);
    return next;
  });
}

/** @param {{ channels: Record<string, number> }} state @param {string} guildId */
export function sumGuildUnread(state, guildId) {
  if (!guildId) return 0;
  const prefix = `${guildId}|`;
  let s = 0;
  for (const [k, v] of Object.entries(state.channels)) {
    if (k.startsWith(prefix)) s += Number(v) || 0;
  }
  return s;
}

/** @param {{ dms: Record<string, number> }} state */
export function sumAllDmUnread(state) {
  let s = 0;
  for (const v of Object.values(state.dms)) {
    s += Number(v) || 0;
  }
  return s;
}

/** @param {{ dms: Record<string, number> }} state @param {string} peerLower */
export function dmUnreadForPeer(state, peerLower) {
  return Number(state.dms[peerLower]) || 0;
}

/** @param {number} n */
export function formatUnreadBadge(n) {
  if (!n || n < 1) return null;
  return n > 9 ? "9+" : String(n);
}

/**
 * Small pill for sidebar channel / DM rows (Discord-style).
 * @param {{ count: number, className?: string }} props
 */
export function SidebarUnreadPill({ count, className = "" }) {
  const label = formatUnreadBadge(count);
  if (!label) return null;
  return <span className={`sidebar-unread-pill ${className}`.trim()}>{label}</span>;
}
